import { useEffect, useRef, useState } from "react";
import type { AgentDTO, ConnectorDTO, SkillDTO, SubjectDTO, SubjectTypeDTO } from "../../../shared/types";
import { KeptFiles } from "../../components/KeptFiles";
import { List, ListRow } from "../../components/List";
import { Dialog } from "../../components/Dialog";
import { RunsList } from "../../components/RunsList";
import { ScopeAgents } from "../../components/ScopeAgents";
import { ScopeIds } from "../../components/ScopeIds";
import { ScopeMemory } from "../../components/ScopeMemory";
import { ScopeActions } from "../../components/ScopeActions";
import { NewSkillForm } from "../../components/SkillForm";
import { Badge, Button, ErrorText, inputCls } from "../../components/ui";
import { get, patch } from "../../lib/api";
import { Link, navigate, useLocation } from "../../lib/router";
import { scopeName, scopePath, typePath, type ScopeDetail } from "../../lib/scopes";

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="sm"
      className="shrink-0"
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

/** The name, which can change any time, and the key, which never does; then archiving it or deleting it. */
function ScopeHeader({ scope, type, onRenamed, onChanged }: { scope: ScopeDetail; type: SubjectTypeDTO; onRenamed: () => void; onChanged: (scope: SubjectDTO) => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(scope.displayName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await patch(`/api/subjects/${scope.id}`, { displayName: name.trim() });
      setEditing(false);
      onRenamed();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="px-4 pt-4 pb-5 md:px-6">
      {editing ? (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <input className={`${inputCls} max-w-md text-base`} value={name} onChange={(e) => setName(e.target.value)} autoFocus aria-label="Name" />
          <Button variant="primary" disabled={busy || !name.trim() || name.trim() === scope.displayName}>Save</Button>
          <Button
            type="button"
            onClick={() => {
              setEditing(false);
              setName(scope.displayName);
            }}
          >
            Cancel
          </Button>
        </form>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="min-w-0 truncate text-title font-semibold tracking-tight">{scopeName(scope)}</h1>
          <Badge>{type.name}</Badge>
          {scope.archivedAt && <Badge tone="amber">Archived</Badge>}
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>Rename</Button>
        </div>
      )}
      <ErrorText error={error} />
      <div className="mt-2 flex min-w-0 items-center gap-2">
        <span className="min-w-0 font-mono text-sm break-all text-muted">{scope.key}</span>
        <CopyButton text={scope.key} />
      </div>
      <p className="mt-2 max-w-3xl text-sm text-muted">
        Every agent working under this scope reads its memory, skills and files, and is shown its id in the tools that agent has.
      </p>
      <ScopeActions scope={scope} onChanged={onChanged} />
    </div>
  );
}

/** The scope's own skills, offered to every agent working under it. */
function ScopeSkills({ scope, agents }: { scope: ScopeDetail; agents: AgentDTO[] }) {
  const [skills, setSkills] = useState<SkillDTO[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = () => get<SkillDTO[]>(`/api/skills?subjectId=${scope.id}`).then(setSkills, (e) => setError(e.message));
  useEffect(() => {
    void load();
  }, [scope.id]);
  return (
    <>
      <List
        title={skills ? `Skills · ${skills.length}` : "Skills"}
        hint="Procedures offered to every agent working under it, on top of the agent's own."
        action={<Button onClick={() => setCreating(true)}>New skill</Button>}
        empty={skills?.length === 0 ? "No skills of its own yet." : undefined}
        footer={error && <ErrorText error={error} />}
      >
        {skills?.map((s) => (
          <ListRow
            key={s.id}
            to={`/admin/skills/${s.id}`}
            title={
              <>
                <span className="truncate font-mono text-sm">{s.name}</span>
                <Badge>v{s.version}</Badge>
              </>
            }
            subtitle={s.description}
          />
        ))}
      </List>
      <Dialog open={creating} onClose={() => setCreating(false)} size="lg" title={`New skill for ${scopeName(scope)}`}>
        <NewSkillForm
          agents={agents}
          scope={scope}
          onCreated={() => {
            setCreating(false);
            void load();
          }}
        />
      </Dialog>
    </>
  );
}

/** One scope: its name and key, its id in each tool, the agents working under it, and its memory, skills and files. */
export function AdminScope({ scopeId }: { scopeId: string }) {
  const { pathname } = useLocation();
  const [scope, setScope] = useState<ScopeDetail | null>(null);
  const [type, setType] = useState<SubjectTypeDTO | null>(null);
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [connectors, setConnectors] = useState<ConnectorDTO[]>([]);
  const [error, setError] = useState<string | null>(null);

  // The scope this page is for now: an answer about another one, after Back or Forward, is dropped.
  const showing = useRef(scopeId);
  showing.current = scopeId;
  const load = () =>
    get<ScopeDetail>(`/api/subjects/${encodeURIComponent(scopeId)}`).then(async (s) => {
      const t = await get<SubjectTypeDTO>(`/api/subject-types/${encodeURIComponent(s.kind)}`);
      if (s.id !== showing.current.toLowerCase()) return;
      setScope(s);
      setType(t);
    }).catch((e) => setError(e.message));
  useEffect(() => {
    setScope(null);
    setError(null);
    void load();
    get<AgentDTO[]>("/api/agents").then(setAgents, () => {});
    get<ConnectorDTO[]>("/api/connectors").then(setConnectors, () => {});
  }, [scopeId]);
  // An old address (/admin/subjects/<id>) or one naming another type moves to the scope's own; a scope still showing
  // from before Back or Forward changed the address never moves it.
  useEffect(() => {
    if (scope && scope.id === scopeId.toLowerCase() && pathname !== scopePath(scope)) navigate(scopePath(scope), true);
  }, [scope, scopeId, pathname]);

  if (!scope || !type) {
    return (
      <div className="space-y-4 px-4 py-8 md:px-6">
        {error ? <ErrorText error={error} /> : <div className="card h-40 animate-pulse" />}
      </div>
    );
  }
  return (
    <div className="pb-12">
      <div className="px-4 pt-5 md:px-6">
        <Link to={typePath(type.key)} className="text-sm text-muted hover:text-fg">← {type.name}</Link>
      </div>
      <ScopeHeader key={scope.updatedAt} scope={scope} type={type} onRenamed={load} onChanged={(s) => setScope({ ...scope, ...s })} />
      <div className="grid grid-cols-1 items-start gap-4 px-4 md:px-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="min-w-0 space-y-4">
          <ScopeMemory subjectId={scope.id} />
          <RunsList key={scope.id} filter={{ subjectId: scope.id }} show="agent" hint="Every run under this scope, whichever agent made it. Open one to see each step it took." />
          <ScopeSkills scope={scope} agents={agents} />
          <KeptFiles on={{ subjectId: scope.id }} hint="Every agent working under it can read these." />
        </div>
        {/* On a phone the ids and the agents come first: they say what this scope is. */}
        <div className="order-first min-w-0 space-y-4 lg:order-none">
          <ScopeIds scope={scope} type={type} connectors={connectors} onChanged={(s) => setScope({ ...scope, ...s })} />
          <ScopeAgents scope={scope} type={type} agents={agents} onChanged={load} />
        </div>
      </div>
    </div>
  );
}
